import { NavigationTabsItem } from '@app/components/ui/navigation-tabs/navigation-tabs';
import { StatisticWidgetsBulkRequestType } from '@app/interfaces/qm-module/qm-statistic/qm-statistic.type';
import { navigationRoutes } from '@app/utils/navigation-routes';
import React, { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export const useGetStatisticLinkObject = (
  setNavigationItems: React.Dispatch<React.SetStateAction<NavigationTabsItem[]>>,
): {
  widgetsFilterBlock: Partial<StatisticWidgetsBulkRequestType> | null;
  updateUrl: (newObject: Partial<StatisticWidgetsBulkRequestType>) => void;
} => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const dataStr = params.get('filter');
  const navigate = useNavigate();
  const isFirstRender = useRef(true);
  const widgetsFilterBlock = React.useMemo(() => {
    if (!dataStr) return null;
    try {
      return JSON.parse(decodeURIComponent(dataStr)) as Partial<StatisticWidgetsBulkRequestType>;
    } catch {
      return null;
    }
  }, [dataStr]);

  const updateUrl = useCallback(
    (newObject: Partial<StatisticWidgetsBulkRequestType>) => {
      const queryString = encodeURIComponent(
        JSON.stringify({ ...widgetsFilterBlock, ...newObject }),
      );
      navigate(`?filter=${queryString}`, { replace: true });
    },
    [navigate, widgetsFilterBlock],
  );

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const newPath =
      location.pathname.replace(`/${navigationRoutes.qm_module_statistic}`, '.') + location.search;

    setNavigationItems((prev) => {
      let hasChanges = false;

      const expectedPathname = location.pathname.replace(
        `/${navigationRoutes.qm_module_statistic}`,
        '',
      );

      const updated = prev.map((route) => {
        if (route.index === 'main') return route;

        const prevUrl = new URL(String(route.index), window.location.origin);

        if (prevUrl.pathname === expectedPathname) {
          if (route.index !== newPath || route.link !== newPath) {
            hasChanges = true;
            return { ...route, index: newPath, link: newPath };
          }
        }
        return route;
      });

      return hasChanges ? updated : prev;
    });
  }, [location.pathname, location.search, setNavigationItems]);

  return { widgetsFilterBlock, updateUrl };
};
